
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { LeadAccount, StrategyProvider } from "@/types/strategy-types";
import { LeadAccountFormFields } from "./form/LeadAccountFormFields";
import { LeadAccountFormActions } from "./form/LeadAccountFormActions";
import { useLeadAccountForm } from "@/hooks/admin/lead-accounts/useLeadAccountForm";
import { useModalStack } from "@/hooks/useModalStack";

interface LeadAccountFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (leadAccount: Omit<LeadAccount, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  initialData?: LeadAccount;
  providers: StrategyProvider[];
  userRole?: string | null;
}

export const LeadAccountFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  providers,
  userRole
}: LeadAccountFormDialogProps) => {
  const {
    formData,
    isSubmitting,
    setIsSubmitting,
    providerId,
    handleChange
  } = useLeadAccountForm(initialData, userRole);
  
  const { handleOpenChange } = useModalStack(isOpen, onClose);
  const dialogDescriptionId = "lead-account-form-description";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby={dialogDescriptionId}>
        <DialogHeader>
          <DialogTitle>{initialData ? "Edit" : "Create"} Lead Account</DialogTitle>
          <DialogDescription id={dialogDescriptionId}>
            {initialData
              ? "Update the lead account details."
              : "Add a new lead account."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <LeadAccountFormFields
            formData={formData}
            handleChange={handleChange}
            providerId={providerId}
            providers={providers}
            userRole={userRole}
          />
          
          <LeadAccountFormActions
            isSubmitting={isSubmitting}
            onClose={onClose}
            initialData={initialData}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadAccountFormDialog;
